/**
 * Planzeilen-Auswahl eines Tages (fuer CheckIn-/ReportAbsence-/Rebook-Dialog):
 * optionaler Schicht-Filter + Select + Kontextanzeige Schicht/Stelle/Arbeitsplatz.
 * Quelle: GET /shift-plan?from=to=Datum&presence=all (+shift).
 */

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { ABSENCE_REASONS } from "@/lib/absenceColors"
import { fmtDate } from "@/lib/format"
import { fetchAllShiftPlanEntries } from "@/lib/shiftPlanQuery"
import { api } from "@/services/api"
import type { ShiftPlanEntry } from "@/types"

interface PlanEntryPickerProps {
  /** Stichtag "YYYY-MM-DD" */
  date: string
  value: ShiftPlanEntry | null
  onChange: (entry: ShiftPlanEntry | null) => void
  /** Bereits eingebuchte Planzeilen ausblenden (CheckIn-Dialog) */
  excludeCheckedIn?: boolean
}

export function PlanEntryPicker({
  date,
  value,
  onChange,
  excludeCheckedIn = false,
}: PlanEntryPickerProps) {
  const [shiftFilter, setShiftFilter] = useState("")

  const shifts = useQuery({
    queryKey: ["shifts", { size: 100 }],
    queryFn: () => api.organization.shifts.list({ size: 100 }),
  })

  const entries = useQuery({
    queryKey: ["shiftPlan", { from: date, to: date, shift: shiftFilter, presence: "all" }],
    queryFn: () =>
      fetchAllShiftPlanEntries({
        from: date,
        to: date,
        shift: shiftFilter ? Number(shiftFilter) : undefined,
        presence: "all",
      }),
    enabled: date !== "",
  })

  const options = (entries.data ?? []).filter((e) => !excludeCheckedIn || !e.isCheckedIn)

  return (
    <>
      <div className="space-y-1.5">
        <Label>Schicht-Filter (optional)</Label>
        <Select
          value={shiftFilter}
          onChange={(e) => {
            setShiftFilter(e.target.value)
            onChange(null)
          }}
        >
          <option value="">Alle Schichten</option>
          {(shifts.data?.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>Planzeile ({fmtDate(date)}) *</Label>
        <Select
          value={value?.id ?? ""}
          onChange={(e) => onChange(options.find((o) => o.id === e.target.value) ?? null)}
        >
          <option value="">
            {entries.isLoading
              ? "Lade Planzeilen …"
              : options.length === 0
                ? "— keine Planzeilen am Stichtag —"
                : "— Planzeile wählen —"}
          </option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.employeeName} — {o.shiftName} / {o.positionName}
            </option>
          ))}
        </Select>
      </div>

      {value && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <p className="font-medium">{value.employeeName}</p>
          <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-muted-foreground">
            <dt>Schicht</dt>
            <dd className="text-foreground">{value.shiftName}</dd>
            <dt>Stelle</dt>
            <dd className="text-foreground">{value.positionName}</dd>
            <dt>Arbeitsplatz</dt>
            <dd className="text-foreground">{value.workplaceName ?? "—"}</dd>
            {value.teamName && (
              <>
                <dt>Team</dt>
                <dd className="text-foreground">{value.teamName}</dd>
              </>
            )}
          </dl>
          {value.absenceReason && (
            <p className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className={ABSENCE_REASONS[value.absenceReason].badge}>
                {ABSENCE_REASONS[value.absenceReason].label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Mitarbeiter ist am Stichtag abwesend gemeldet.
              </span>
            </p>
          )}
          {value.isCheckedIn && (
            <p className="mt-2 text-xs text-muted-foreground">Bereits eingebucht.</p>
          )}
        </div>
      )}
    </>
  )
}
